import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Stack, Button, TextField, MenuItem, Chip,
  FormControlLabel, Checkbox, Alert, Divider, IconButton, Grid, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Visibility as PreviewIcon,
  Refresh as RefreshIcon,
  AutoAwesome as EnrichIcon,
  RuleFolder as RecheckIcon,
  OpenInNew as OpenInNewIcon,
  Delete as DeleteIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  previewAutoEnrichment, startAutoEnrichment, listAutoEnrichmentRuns,
  getAiProviders, recheckLeadVerdicts, deleteAutoEnrichmentRun, getLeadSegmentations,
  getLeadSourceDetails,
} from '../services/api';
import { useDialog } from '../components/DialogProvider';
import AzotteGrid from '../components/AzotteGrid';

import { LEAD_STATUS_OPTIONS } from '../constants/leadStatus';
const STATUSES = ['', ...LEAD_STATUS_OPTIONS.map(o => o.value)];
const SOURCES = ['', 'ManualEntry', 'CsvImport', 'Google', 'LinkedIn', 'CrunchBase', 'Owler', 'Referral', 'Other', 'AiSearch'];
const ICP_FITS = [
  { value: '', label: '— Any —' },
  { value: 'CurrentFit', label: 'Current Fit' },
  { value: 'FuturePotential', label: 'Future Potential' },
  { value: 'NotAFit', label: 'Not a Fit' },
  { value: 'None', label: 'Not verdicted' },
];

const STATUS_CHIP_COLOR = {
  Pending: 'default',
  Running: 'info',
  Cancelling: 'warning',
  Completed: 'success',
  Failed: 'error',
  Cancelled: 'warning',
};

const defaultFilter = {
  search: '',
  status: '',
  segmentationId: '',
  source: '',
  sourceDetail: '',
  aiIcpFit: '',
  includeArchived: false,
  idFrom: '',
  idTo: '',
  createdAfter: '',
  createdBefore: '',
  limit: '',
};

function buildFilter(f) {
  return {
    search: f.search?.trim() || null,
    status: f.status || null,
    segmentationId: f.segmentationId ? parseInt(f.segmentationId, 10) : null,
    source: f.source || null,
    sourceDetail: f.sourceDetail || null,
    aiIcpFit: f.aiIcpFit || null,
    includeArchived: !!f.includeArchived,
    idFrom: f.idFrom ? parseInt(f.idFrom, 10) : null,
    idTo: f.idTo ? parseInt(f.idTo, 10) : null,
    createdAfter: f.createdAfter ? new Date(f.createdAfter).toISOString() : null,
    createdBefore: f.createdBefore ? new Date(f.createdBefore).toISOString() : null,
    limit: f.limit ? parseInt(f.limit, 10) : null,
  };
}

function fmtSecs(s) {
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

function fmtDuration(start, end, accumulatedSeconds) {
  const acc = accumulatedSeconds || 0;
  if (end) {
    if (acc > 0) return fmtSecs(acc);
    if (start) {
      const s = Math.max(0, Math.round((new Date(end).getTime() - new Date(start).getTime()) / 1000));
      return fmtSecs(s);
    }
    return '—';
  }
  if (!start) return acc > 0 ? fmtSecs(acc) : '—';
  const live = Math.max(0, Math.round((Date.now() - new Date(start).getTime()) / 1000));
  return fmtSecs(acc + live);
}

export default function AutoEnrichmentPage() {
  const navigate = useNavigate();
  const dialog = useDialog();
  const [filter, setFilter] = useState(defaultFilter);
  const [segmentations, setSegmentations] = useState([]);
  const [sourceDetails, setSourceDetails] = useState([]);
  const [providers, setProviders] = useState([]);
  const [providerName, setProviderName] = useState('gemini');
  const [intervalSeconds, setIntervalSeconds] = useState('30');
  const [preview, setPreview] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState({ preview: false, start: false, recheck: false });
  const [error, setError] = useState('');
  const [recheck, setRecheck] = useState(null);
  const [filterOpen, setFilterOpen] = useState(true);
  const [newDialogOpen, setNewDialogOpen] = useState(false);

  const activeFilterCount = [
    filter.search, filter.status, filter.segmentationId, filter.source, filter.sourceDetail, filter.aiIcpFit,
    filter.idFrom, filter.idTo, filter.createdAfter, filter.createdBefore, filter.limit,
  ].filter(Boolean).length + (filter.includeArchived ? 1 : 0);

  const loadRecent = useCallback(async () => {
    try {
      const { data } = await listAutoEnrichmentRuns(20);
      setRecent(data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getAiProviders();
        const list = Array.isArray(data) ? data : [];
        setProviders(list);
        const hasGemini = list.some((p) => (p.name || '').toLowerCase() === 'gemini');
        if (!hasGemini && list.length > 0) {
          const def = list.find((p) => p.isDefault) || list[0];
          setProviderName((def.name || '').toLowerCase());
        }
      } catch { /* ignore — server may not have endpoint */ }
      try {
        const { data } = await getLeadSegmentations();
        setSegmentations(Array.isArray(data) ? data : (data?.items || []));
      } catch { /* ignore */ }
      try {
        const { data } = await getLeadSourceDetails();
        setSourceDetails(Array.isArray(data) ? data : []);
      } catch { /* ignore */ }
    })();
    loadRecent();
    const t = setInterval(loadRecent, 5000);
    return () => clearInterval(t);
  }, [loadRecent]);

  const handlePreview = async () => {
    setLoading((s) => ({ ...s, preview: true }));
    setError('');
    try {
      const { data } = await previewAutoEnrichment(buildFilter(filter));
      setPreview(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Preview failed');
    } finally {
      setLoading((s) => ({ ...s, preview: false }));
    }
  };

  const handleRecheck = async (dryRun) => {
    if (!dryRun) {
      const ok = await dialog.confirm({
        title: 'Apply verdict recheck',
        message: 'Re-applies the AI-verdict → status routing for every lead with an AiIcpFit set.\n\n• CurrentFit  → Qualified  (rescues leads disqualified by domain check)\n• FuturePotential (no partner) → Qualified  (promotes legacy Parked leads — outreach-worthy)\n• FuturePotential / NotAFit + Partner=Yes → PartnerCandidate\n• NotAFit (no partner) → Disqualified\n\nQualified / InOutreach / Converted / Archived are not touched.',
        confirmText: 'Apply',
      });
      if (!ok) return;
    }
    setLoading((s) => ({ ...s, recheck: true }));
    setError('');
    try {
      const { data } = await recheckLeadVerdicts({ dryRun, rescueDisqualified: true });
      setRecheck(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Recheck failed');
    } finally {
      setLoading((s) => ({ ...s, recheck: false }));
    }
  };

  const handleDeleteRun = async (row) => {
    const ok = await dialog.confirm({
      title: 'Delete enrichment job',
      message: `Delete job #${row.id}? This permanently removes the job and every per-lead result row. The leads themselves are not affected.`,
      confirmText: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteAutoEnrichmentRun(row.id);
      await loadRecent();
    } catch (e) {
      setError(e?.response?.data?.message || 'Delete failed');
    }
  };

  const handleStart = async () => {
    const count = preview?.matchCount;
    const interval = intervalSeconds ? parseInt(intervalSeconds, 10) : 30;
    const msg = count != null
      ? `Start auto-enrichment on ${count} leads using ${providerName || 'default'}? Each lead will be passed to AI with a ${interval}s gap. Suggestions will be auto-applied.`
      : 'Start auto-enrichment on all matching leads (no preview)?';
    if (!(await dialog.confirm({ title: 'Start auto-enrichment', message: msg, confirmText: 'Start' }))) return;

    setLoading((s) => ({ ...s, start: true }));
    setError('');
    try {
      const { data } = await startAutoEnrichment({
        filter: buildFilter(filter),
        providerName: providerName || null,
        intervalSeconds: interval,
      });
      navigate(`/leads/auto-enrichment/${data.id}`);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to start enrichment job');
    } finally {
      setLoading((s) => ({ ...s, start: false }));
    }
  };

  const previewColumns = [
    { key: 'id', header: 'ID', render: (v) => `#${v}` },
    { key: 'companyName', header: 'Company', render: (v) => <Typography variant="body2" sx={{ fontWeight: 600 }}>{v}</Typography> },
    {
      key: 'website', header: 'Website', hideOnMobile: true,
      render: (v) => v ? <Typography variant="body2" sx={{ color: '#6366f1', wordBreak: 'break-all' }}>{v}</Typography> : <em style={{ color: '#94a3b8' }}>—</em>,
    },
    { key: 'status', header: 'Status', hideOnMobile: true },
    { key: 'source', header: 'Source', hideOnMobile: true, hideOnTablet: true },
    { key: 'country', header: 'Country', hideOnMobile: true, hideOnTablet: true, render: (v) => v || '—' },
    {
      key: 'createdAt', header: 'Created', hideOnMobile: true, hideOnTablet: true,
      render: (v) => v ? new Date(v).toLocaleDateString() : '—',
    },
    { key: 'enrichmentCompleteness', header: 'Enrich %', render: (v) => `${v ?? 0}%` },
  ];

  const recentColumns = [
    { key: 'id', header: 'ID', render: (v) => <Typography variant="body2" sx={{ fontWeight: 600 }}>#{v}</Typography> },
    {
      key: 'status', header: 'Status',
      render: (v) => <Chip size="small" label={v} color={STATUS_CHIP_COLOR[v] || 'default'} sx={{ fontWeight: 600 }} />,
    },
    {
      key: 'providerName', header: 'Provider', hideOnMobile: true,
      render: (v) => v || <em style={{ color: '#94a3b8' }}>default</em>,
    },
    {
      key: 'intervalSeconds', header: 'Interval', hideOnMobile: true, hideOnTablet: true,
      render: (v) => `${v}s`,
    },
    {
      key: 'startedAt', header: 'Started', hideOnMobile: true,
      render: (v) => v ? new Date(v).toLocaleString() : '—',
    },
    {
      key: 'duration', header: 'Duration', hideOnMobile: true,
      render: (_, row) => fmtDuration(row.startedAt, row.finishedAt, row.accumulatedSeconds),
    },
    { key: 'total', header: 'Total', hideOnMobile: true, hideOnTablet: true },
    { key: 'processed', header: 'Done' },
    {
      key: 'succeeded', header: 'OK', hideOnMobile: true, hideOnTablet: true,
      render: (v) => <Box component="span" sx={{ color: '#16a34a', fontWeight: 600 }}>{v}</Box>,
    },
    {
      key: 'failed', header: 'Fail', hideOnMobile: true, hideOnTablet: true,
      render: (v) => <Box component="span" sx={{ color: v > 0 ? '#dc2626' : '#94a3b8', fontWeight: 600 }}>{v}</Box>,
    },
    {
      key: 'icpCurrentFit', header: 'Current', hideOnMobile: true, hideOnTablet: true,
      render: (v) => <Box component="span" sx={{ color: '#16a34a', fontWeight: 700 }}>{v ?? 0}</Box>,
    },
    {
      key: 'icpFuturePotential', header: 'Future', hideOnMobile: true, hideOnTablet: true,
      render: (v) => <Box component="span" sx={{ color: '#0284c7', fontWeight: 700 }}>{v ?? 0}</Box>,
    },
    {
      key: 'icpNotAFit', header: 'Not fit', hideOnMobile: true, hideOnTablet: true,
      render: (v) => <Box component="span" sx={{ color: v > 0 ? '#dc2626' : '#94a3b8', fontWeight: 700 }}>{v ?? 0}</Box>,
    },
    {
      key: 'createdByUsername', header: 'By', hideOnMobile: true, hideOnTablet: true,
      render: (v) => v || '—',
    },
    {
      key: 'actions', header: '',
      render: (_, row) => {
        const canDelete = ['Completed', 'Cancelled', 'Failed'].includes(row.status);
        return (
          <Stack direction="row" spacing={0.75}>
            <Button
              size="small" variant="outlined"
              startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
              onClick={() => navigate(`/leads/auto-enrichment/${row.id}`)}
              sx={{ textTransform: 'none', borderRadius: 1.5 }}
            >
              View
            </Button>
            {canDelete && (
              <Tooltip title="Delete job">
                <IconButton size="small" color="error" onClick={() => handleDeleteRun(row)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: { xs: 2, md: 3 } }}>
        <Typography sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          <EnrichIcon color="primary" /> Auto Enrichment
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.25 }}>
          Run AI enrichment over a filtered set of leads as a background job. Suggestions are auto-applied.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Top CTA */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <Button
          variant="contained" size="medium"
          startIcon={<AddIcon />}
          onClick={() => setNewDialogOpen(true)}
          sx={{
            textTransform: 'none', borderRadius: 1.5, fontWeight: 600,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            boxShadow: '0 2px 6px rgba(99,102,241,0.35)',
            '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' },
          }}
        >
          New auto-enrichment
        </Button>
        {activeFilterCount > 0 && (
          <Chip
            size="small"
            label={`${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''} set`}
            onClick={() => setNewDialogOpen(true)}
            sx={{ bgcolor: '#eef2ff', color: '#4338ca', fontWeight: 600, cursor: 'pointer' }}
          />
        )}
      </Stack>

      {/* New auto-enrichment dialog */}
      <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
          <EnrichIcon color="primary" />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>New auto-enrichment job</Typography>
            <Typography variant="caption" color="text.secondary">
              Pick a lead set, choose AI engine, and start.
            </Typography>
          </Box>
          <IconButton onClick={() => setNewDialogOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ borderBottom: '1px solid #e2e8f0' }}>
            <Stack
              direction="row" alignItems="center" spacing={1}
              onClick={() => setFilterOpen((o) => !o)}
              sx={{
                px: 2, py: 1.25, cursor: 'pointer', userSelect: 'none',
                bgcolor: filterOpen ? '#f8fafc' : 'transparent',
                '&:hover': { bgcolor: '#f8fafc' },
              }}
            >
              <FilterListIcon sx={{ fontSize: 18, color: '#475569' }} />
              <Typography variant="subtitle2" sx={{ color: '#0f172a', fontWeight: 600 }}>Filter leads</Typography>
              {activeFilterCount > 0 && (
                <Chip size="small" label={activeFilterCount}
                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#eef2ff', color: '#4338ca' }} />
              )}
              <Box sx={{ flex: 1 }} />
              {activeFilterCount > 0 && (
                <Button
                  size="small"
                  onClick={(e) => { e.stopPropagation(); setFilter(defaultFilter); setPreview(null); }}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Clear
                </Button>
              )}
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); setFilterOpen((o) => !o); }}
                sx={{ transform: filterOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Collapse in={filterOpen} unmountOnExit>
              <Box sx={{ p: { xs: 2, md: 2.5 } }}>
                <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth size="small"
                  label="Search (company / website / country / tags)"
                  value={filter.search}
                  onChange={(e) => setFilter((f) => ({ ...f, search: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4, md: 2 }}>
                <TextField fullWidth size="small" select label="Status" value={filter.status}
                  onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}>
                  {STATUSES.map((s) => <MenuItem key={s || 'any'} value={s}>{s || '— Any —'}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <TextField fullWidth size="small" select label="Segmentation" value={filter.segmentationId}
                  onChange={(e) => setFilter((f) => ({ ...f, segmentationId: e.target.value }))}
                  SelectProps={{ displayEmpty: true }}>
                  <MenuItem value="">— Any —</MenuItem>
                  {segmentations.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <TextField fullWidth size="small" select label="Source" value={filter.source}
                  onChange={(e) => setFilter((f) => ({ ...f, source: e.target.value }))}>
                  {SOURCES.map((s) => <MenuItem key={s || 'any'} value={s}>{s || '— Any —'}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField fullWidth size="small" select label="Source Name" value={filter.sourceDetail}
                  onChange={(e) => setFilter((f) => ({ ...f, sourceDetail: e.target.value }))}
                  SelectProps={{ displayEmpty: true }}
                  disabled={sourceDetails.length === 0}>
                  <MenuItem value="">— Any —</MenuItem>
                  {sourceDetails.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <TextField fullWidth size="small" select label="ICP Fit" value={filter.aiIcpFit}
                  onChange={(e) => setFilter((f) => ({ ...f, aiIcpFit: e.target.value }))}
                  SelectProps={{ displayEmpty: true }}>
                  {ICP_FITS.map((o) => <MenuItem key={o.value || 'any'} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>

              <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                <TextField fullWidth size="small" type="number" label="ID from" value={filter.idFrom}
                  onChange={(e) => setFilter((f) => ({ ...f, idFrom: e.target.value }))} />
              </Grid>
              <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                <TextField fullWidth size="small" type="number" label="ID to" value={filter.idTo}
                  onChange={(e) => setFilter((f) => ({ ...f, idTo: e.target.value }))} />
              </Grid>
              <Grid size={{ xs: 6, sm: 3, md: 3 }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500, display: 'block', mb: 0.25 }}>
                  Created after
                </Typography>
                <TextField fullWidth size="small" type="date"
                  value={filter.createdAfter}
                  onChange={(e) => setFilter((f) => ({ ...f, createdAfter: e.target.value }))} />
              </Grid>
              <Grid size={{ xs: 6, sm: 3, md: 3 }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500, display: 'block', mb: 0.25 }}>
                  Created before
                </Typography>
                <TextField fullWidth size="small" type="date"
                  value={filter.createdBefore}
                  onChange={(e) => setFilter((f) => ({ ...f, createdBefore: e.target.value }))} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField fullWidth size="small" type="number" label="Limit" inputProps={{ min: 1, max: 5000 }}
                  value={filter.limit}
                  onChange={(e) => setFilter((f) => ({ ...f, limit: e.target.value }))} />
              </Grid>
            </Grid>

            <FormControlLabel
              control={<Checkbox size="small" checked={filter.includeArchived}
                onChange={(e) => setFilter((f) => ({ ...f, includeArchived: e.target.checked }))} />}
              label={<Typography variant="body2">Include Archived &amp; Parked statuses</Typography>}
            />

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#475569' }}>AI engine &amp; pacing</Typography>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" select label="AI engine" value={providerName}
                  onChange={(e) => setProviderName(e.target.value)}>
                  {(providers.length > 0
                    ? providers.map((p) => ({ value: (p.name || '').toLowerCase(), label: p.displayName || p.name }))
                    : [
                        { value: 'gemini', label: 'Gemini (default)' },
                        { value: 'openai', label: 'OpenAI' },
                      ]
                  ).map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" type="number" label="Interval between leads (sec)"
                  inputProps={{ min: 0, max: 3600 }}
                  value={intervalSeconds}
                  onChange={(e) => setIntervalSeconds(e.target.value)}
                  helperText="Wait this long before sending each next lead to AI" />
              </Grid>
            </Grid>

              </Box>
            </Collapse>
          </Box>
          {preview && (
            <Box sx={{ p: { xs: 2, md: 2.5 } }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#475569' }}>
                Preview: <strong>{preview.matchCount}</strong> matching leads (showing up to 50)
              </Typography>
              {preview.sample && preview.sample.length > 0 ? (
                <AzotteGrid
                  columns={previewColumns}
                  rows={preview.sample}
                  totalCount={preview.sample.length}
                  showPagination={false}
                  showToolbar={false}
                />
              ) : (
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>No leads match the filter.</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, gap: 1, flexWrap: 'wrap', '& .MuiButton-root': { borderRadius: 1.5, textTransform: 'none' } }}>
          <Button size="small" onClick={() => { setFilter(defaultFilter); setPreview(null); }}>
            Reset
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={() => setNewDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="outlined" size="small" startIcon={<PreviewIcon />}
            onClick={handlePreview} disabled={loading.preview}>
            {loading.preview ? 'Counting…' : 'Preview match'}
          </Button>
          <Button variant="contained" size="small" startIcon={<PlayIcon />}
            onClick={handleStart} disabled={loading.start}
            sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' } }}>
            {loading.start ? 'Starting…' : 'Start enrichment'}
          </Button>
        </DialogActions>
      </Dialog>

      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5} sx={{ mb: recheck ? 1.5 : 0 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ color: '#475569', display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <RecheckIcon fontSize="small" /> Recheck verdicts → status
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              Re-applies AI-verdict routing to leads with an AiIcpFit. Rescues domain-disqualified leads (CurrentFit) and promotes legacy Parked leads (FuturePotential, no partner) to Qualified.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0, '& .MuiButton-root': { borderRadius: 1.5, textTransform: 'none' } }}>
            <Button size="small" variant="outlined" onClick={() => handleRecheck(true)} disabled={loading.recheck}>
              {loading.recheck ? 'Working…' : 'Dry run'}
            </Button>
            <Button size="small" variant="contained" color="primary" onClick={() => handleRecheck(false)} disabled={loading.recheck}>
              Apply
            </Button>
          </Stack>
        </Stack>
        {recheck && (
          <Alert severity={recheck.changed > 0 ? 'success' : 'info'} sx={{ mb: 0 }} onClose={() => setRecheck(null)}>
            <strong>{recheck.dryRun ? 'Dry run' : 'Applied'}:</strong> scanned {recheck.scanned} · {recheck.changed} {recheck.dryRun ? 'would change' : 'changed'}
            {recheck.transitions && Object.keys(recheck.transitions).length > 0 && (
              <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {Object.entries(recheck.transitions).map(([k, v]) => (
                  <Chip key={k} size="small" label={`${k}: ${v}`} variant="outlined" />
                ))}
              </Box>
            )}
            {recheck.sample && recheck.sample.length > 0 && (
              <Box sx={{ mt: 1, fontSize: 12, color: '#475569' }}>
                Sample: {recheck.sample.slice(0, 8).map((s) => `#${s.leadId} ${s.companyName} (${s.from}→${s.to})`).join(', ')}{recheck.sample.length > 8 ? '…' : ''}
              </Box>
            )}
          </Alert>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ color: '#475569', flex: 1 }}>Recent runs</Typography>
          <IconButton size="small" onClick={loadRecent} title="Refresh"><RefreshIcon fontSize="small" /></IconButton>
        </Stack>
        <AzotteGrid
          columns={recentColumns}
          rows={recent}
          totalCount={recent.length}
          showPagination={false}
          showToolbar={false}
          onDoubleClick={(r) => navigate(`/leads/auto-enrichment/${r.id}`)}
          actionColumnKey="actions"
        />
      </Paper>
    </Box>
  );
}
